import { useEffect, useState } from 'react';
import type { Pagina } from '@kodica/shared';

import {
  Boton,
  Buscador,
  Cargando,
  Celda,
  Chip,
  Encabezado,
  ErrorCaja,
  Fila,
  Paginador,
  Panel,
  Seleccion,
  Vacio,
} from '../componentes/ui';
import { ErrorApi, pedir } from '../lib/api';
import { consulta, useRecurso, useRetardo } from '../lib/consultas';

/**
 * Quiénes están adentro del fraccionamiento, ahora mismo.
 *
 * Es el reporte que la administración pide primero. Responde a la pregunta que
 * de verdad se hace: «un residente dice que alguien anda dando vueltas, ¿quién
 * hay adentro?».
 *
 * **Solo visitas.** Los residentes entran y salen a diario con su tag o su cara
 * y sus accesos se cierran solos; mezclarlos ahogaría en ruido la lista corta
 * que sí hay que atender.
 *
 * Y esa lista **no siempre es corta**: quien se fue sin escanear se queda aquí.
 * No se barren solos a propósito —un barrido inventaría una hora de salida que
 * nadie vio, y esa hora acabaría en una aclaración como si fuera un hecho— así
 * que se enseñan, se ordenan y se cierran a mano. De ahí que la pantalla venga
 * paginada y ordenable desde el principio: la primera vez que se abrió sobre
 * datos reales tenía cincuenta y seis renglones.
 */

interface Visita {
  id: string;
  nombreVisitante: string | null;
  entradaEn: string;
  origen: string;
  placa: string | null;
  observaciones: string | null;
  minutosDentro: number;
  unidad: { clave: string } | null;
  tipoVisita: { nombre: string; color: string | null } | null;
  puntoAcceso: { nombre: string } | null;
  carril: { nombre: string } | null;
}

const PASE: Record<string, string> = {
  KIOSCO_QR: 'Con invitación',
  MANUAL: 'Sin invitación',
  FACIAL: 'Reconocimiento facial',
  TAG_UHF: 'Tag del coche',
};

/**
 * A partir de cuánto una visita deja de ser creíble.
 *
 * Ocho horas es una jornada: una visita puede durar eso —una obra, una mudanza—
 * así que no se marca como error sino como «mírala». Ponerlo más bajo llenaría
 * la pantalla de avisos y enseñaría a ignorarlos.
 */
const HORAS_SOSPECHOSAS = 8;

/** Las columnas por las que se puede ordenar, y cómo se llaman en el API. */
const COLUMNAS: { clave: string | null; texto: string }[] = [
  { clave: 'nombre', texto: 'Quién' },
  { clave: 'unidad', texto: 'Va a' },
  { clave: 'entrada', texto: 'Entró' },
  // «Lleva» ordena por la misma columna al revés: cuanto más antigua la
  // entrada, más tiempo lleva dentro.
  { clave: 'entrada', texto: 'Lleva' },
  { clave: 'pase', texto: 'Pase' },
  { clave: 'acceso', texto: 'Por dónde' },
  { clave: null, texto: '' },
];

const TAMANOS = [10, 25, 50, 100];

export function AdentroPagina() {
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);
  const [orden, setOrden] = useState('entrada');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [buscar, setBuscar] = useState('');
  const [vuelta, setVuelta] = useState(0);
  const [cerrando, setCerrando] = useState<string | null>(null);

  const busqueda = useRetardo(buscar);
  const ruta = consulta('/accesos/adentro', {
    pagina,
    porPagina,
    orden,
    dir,
    buscar: busqueda,
    v: vuelta,
  });
  const { datos, cargando, error, recargar } = useRecurso<Pagina<Visita>>(ruta);

  // La caseta deja esta pantalla puesta: tiene que envejecer sola.
  useEffect(() => {
    const t = setInterval(() => setVuelta((v) => v + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  function ordenarPor(clave: string) {
    if (orden === clave) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrden(clave);
      setDir('asc');
    }
    setPagina(1);
  }

  async function marcarSalida(v: Visita) {
    const quien = v.nombreVisitante ?? 'esta visita';
    if (
      !window.confirm(
        `¿Marcar la salida de ${quien} a mano? Quedará registrado que la cerraste tú, y a qué hora.`,
      )
    )
      return;
    setCerrando(v.id);
    try {
      await pedir(`/accesos/${v.id}/salida`, { metodo: 'POST' });
      recargar();
    } catch (e) {
      window.alert(e instanceof ErrorApi ? e.message : 'No se pudo marcar la salida');
    } finally {
      setCerrando(null);
    }
  }

  if (cargando && !datos) return <Cargando />;
  if (error) return <ErrorCaja>{error}</ErrorCaja>;

  const visitas = datos?.datos ?? [];

  return (
    <>
      <Encabezado
        titulo="Adentro ahora"
        descripcion="Visitas que entraron y todavía no han salido. Los residentes no se listan."
      >
        {datos && datos.total > 0 && <Chip tono="activo">{datos.total}</Chip>}
      </Encabezado>

      <div className="mb-stack-md flex flex-col gap-stack-md sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full sm:max-w-sm">
          <Buscador
            value={buscar}
            onChange={(e) => {
              setBuscar(e.target.value);
              setPagina(1);
            }}
            placeholder="Buscar por nombre, placa o vivienda"
          />
        </div>
        <label className="flex items-center gap-2 text-label-sm text-on-surface-variant">
          Por página
          <div className="w-24">
            <Seleccion
              value={String(porPagina)}
              onChange={(e) => {
                setPorPagina(Number(e.target.value));
                setPagina(1);
              }}
            >
              {TAMANOS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Seleccion>
          </div>
        </label>
      </div>

      <Panel>
        {visitas.length === 0 ? (
          <Vacio>
            {busqueda ? `Nada coincide con «${busqueda}».` : 'No hay ninguna visita adentro.'}
          </Vacio>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="border-b border-outline-variant">
                  <tr>
                    {COLUMNAS.map((c, i) => (
                      <th
                        key={i}
                        className="px-5 py-3 text-label-bold uppercase tracking-wider text-on-surface-variant"
                      >
                        {c.clave ? (
                          <button
                            type="button"
                            onClick={() => ordenarPor(c.clave!)}
                            className="inline-flex items-center gap-1 hover:text-on-surface"
                          >
                            {c.texto}
                            {/* La flecha solo en la columna activa: marcarlas
                                todas convierte el encabezado en ruido y deja de
                                decir por cuál se está ordenando. */}
                            {orden === c.clave && (
                              <span aria-hidden>{dir === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </button>
                        ) : (
                          c.texto
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {visitas.map((v) => (
                    <Fila key={v.id}>
                      <Celda fuerte>
                        {v.nombreVisitante ?? 'Sin nombre'}
                        {v.placa && <span className="ml-2 font-mono text-label-sm">{v.placa}</span>}
                      </Celda>
                      <Celda>{v.unidad?.clave ?? '—'}</Celda>
                      <Celda>{new Date(v.entradaEn).toLocaleString('es-MX')}</Celda>
                      <Celda>
                        <Tiempo minutos={v.minutosDentro} />
                      </Celda>
                      <Celda>{PASE[v.origen] ?? v.origen.toLowerCase()}</Celda>
                      <Celda>
                        {[v.puntoAcceso?.nombre, v.carril?.nombre].filter(Boolean).join(' · ') ||
                          '—'}
                      </Celda>
                      <Celda>
                        <div className="flex justify-end">
                          <Boton
                            variante="texto"
                            disabled={cerrando === v.id}
                            onClick={() => void marcarSalida(v)}
                          >
                            {cerrando === v.id ? 'Cerrando…' : 'Marcar salida'}
                          </Boton>
                        </div>
                      </Celda>
                    </Fila>
                  ))}
                </tbody>
              </table>
            </div>
            {datos && (
              <Paginador
                pagina={datos.pagina}
                paginas={datos.paginas}
                total={datos.total}
                alCambiar={setPagina}
              />
            )}
          </>
        )}
      </Panel>

      <p className="mt-stack-md text-body-md text-on-surface-variant">
        Las visitas de más de {HORAS_SOSPECHOSAS} horas casi siempre son de alguien que se fue sin
        escanear —a pie, o de copiloto con un residente—. Ciérralas a mano cuando sepas que ya no
        está: si no, esta lista deja de servir.
      </p>
    </>
  );
}

/** Cuánto lleva dentro, en algo que se lee de un vistazo. */
function Tiempo({ minutos }: { minutos: number }) {
  const horas = Math.floor(minutos / 60);
  const texto = horas > 0 ? `${horas} h ${minutos % 60} min` : `${minutos} min`;
  // Ámbar y no rojo: una visita larga no es un fallo, es algo que hay que
  // mirar. El rojo aquí enseñaría a ignorar el rojo.
  return horas >= HORAS_SOSPECHOSAS ? <Chip tono="aviso">{texto}</Chip> : <span>{texto}</span>;
}
